import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Headphones, Timer } from "lucide-react";
import { defineModuleUi, from, unwrap, useOrg } from "@vixio/data";
import { formatDateTime, formatInt, registerAuditLabels, lazyPage } from "@vixio/ui";
import "./api";
import type { TicketRow } from "./api";

export * from "./api";

// Telas carregadas sob demanda: cada uma vira um arquivo à parte no build.
const TicketsPage = lazyPage(() => import("./TicketsPage"), "TicketsPage");
const TicketPage = lazyPage(() => import("./TicketPage"), "TicketPage");
const SupportSettingsPage = lazyPage(() => import("./SettingsPage"), "SupportSettingsPage");

registerAuditLabels({
  "support.ticket_opened": "Chamado aberto",
  "support.ticket_status": "Situação do chamado alterada",
  "support.ticket_reclassified": "Chamado reclassificado",
  "support.queue_upserted": "Fila de chamados salva",
  "support.sla_set": "Prazo de SLA alterado",
  "support.settings_set": "Expediente de suporte alterado",
  "support.defaults_installed": "Filas e SLA padrão instalados",
});

function SupportWidget() {
  const org = useOrg();
  const q = useQuery({
    queryKey: ["support", "widget", org.id],
    queryFn: () =>
      unwrap<Array<Pick<TicketRow, "id" | "number" | "subject" | "sla_state" | "next_due">>>(
        from("support", "v_tickets")
          .select("id, number, subject, sla_state, next_due")
          .eq("org_id", org.id)
          .in("status", ["open", "in_progress", "waiting_client"])
          .order("next_due", { ascending: true, nullsFirst: false })
          .limit(200),
      ),
  });
  const rows = q.data ?? [];
  const hot = rows.filter((r) => r.sla_state !== "ok").slice(0, 4);
  return (
    <div className="vx-stack">
      <dl className="vx-dl">
        <dt>Em aberto</dt>
        <dd className="num">{formatInt(rows.length)}</dd>
        <dt>Prazo estourado</dt>
        <dd className={rows.some((r) => r.sla_state === "breached") ? "num text-crit" : "num"}>{formatInt(rows.filter((r) => r.sla_state === "breached").length)}</dd>
      </dl>
      {hot.map((r) => (
        <div key={r.id} className="vx-row vx-row--between">
          <Link to={`/entrega/chamados/${r.id}`}>{r.subject}</Link>
          <span className={r.sla_state === "breached" ? "text-crit num" : "text-warn num"}>{formatDateTime(r.next_due)}</span>
        </div>
      ))}
      <Link to="/entrega/chamados">Abrir chamados</Link>
    </div>
  );
}

export default defineModuleUi({
  key: "support",
  title: "Chamados",
  domain: "delivery",
  icon: Headphones,
  menu: [
    { path: "/entrega/chamados", label: "Chamados", minRole: "member", icon: Headphones, order: 40, keywords: "suporte sla correção operação evolução fila prioridade" },
    { path: "/entrega/sla", label: "Filas e SLA", minRole: "member", icon: Timer, order: 45, keywords: "expediente prazo resposta solução escalonamento" },
  ],
  routes: [
    { path: "/entrega/chamados", element: <TicketsPage />, minRole: "member" },
    { path: "/entrega/chamados/:id", element: <TicketPage />, minRole: "member" },
    { path: "/entrega/sla", element: <SupportSettingsPage />, minRole: "member" },
  ],
  widgets: [{ key: "support.sla", title: "Chamados", minRole: "member", span: 1, order: 36, element: SupportWidget }],
});
